/* Feb-2026 — Admin Troubleshooting: AI-generated novels picker.
 *
 * A filtered sibling to AdminLoomManuscriptPicker.jsx, scoped to
 * bookstore catalog titles flagged `is_ai_generated` — both still-in-
 * progress drafts AND already-published storefront titles. Selecting
 * one opens its underlying Loom project the same way — via the
 * admin-loom-bridge sandbox session — so staff always have full Loom
 * access (chapters, story bible, AI tools) to polish or rewrite any
 * AI-written novel, whether it's live or not.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { ChevronsUpDown, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { adminApi } from '@/lib/api';

export default function AdminAiNovelsPicker() {
  const [open, setOpen] = useState(false);
  const [novels, setNovels] = useState(null);
  const [opening, setOpening] = useState(null);

  const load = useCallback(() => {
    adminApi.aiDraftNovels().then((d) => setNovels(d.novels || [])).catch(() => setNovels([]));
  }, []);

  useEffect(() => { if (open && novels === null) load(); }, [open, novels, load]);

  const openNovel = async (n) => {
    setOpening(n.id);
    try {
      await adminApi.loomBridge(n.source_project_id);
      window.location.href = `/app?project=${encodeURIComponent(n.source_project_id)}`;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not open this novel in The Loom.');
      setOpening(null);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost" size="sm" className="h-7 gap-1.5 text-[12px]"
          data-testid="admin-hub-open-ai-novels"
        >
          <Sparkles className="h-3.5 w-3.5" /> AI Novels <ChevronsUpDown className="h-3 w-3 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end" data-testid="admin-ai-novels-picker">
        <Command>
          <CommandInput placeholder="Search AI-generated titles…" data-testid="admin-ai-novels-search" />
          <CommandList>
            {novels === null ? (
              <div className="flex items-center justify-center gap-2 py-6 text-[12px] text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading AI novels…
              </div>
            ) : (
              <>
                <CommandEmpty>No AI-generated novels found.</CommandEmpty>
                <CommandGroup heading={`${novels.length} AI-generated novel${novels.length === 1 ? '' : 's'}`}>
                  {novels.map((n) => (
                    <CommandItem
                      key={n.id}
                      value={`${n.title} ${n.author}`}
                      onSelect={() => openNovel(n)}
                      disabled={opening === n.id}
                      data-testid={`admin-ai-novels-option-${n.id}`}
                      className="flex items-center justify-between gap-2"
                    >
                      <span className="flex min-w-0 flex-col">
                        <span className="truncate font-medium">{n.title}</span>
                        <span className="truncate text-[11px] opacity-70">{n.author} · {n.genre_slug}</span>
                      </span>
                      <span className="flex shrink-0 items-center gap-1.5">
                        <span
                          data-testid={`admin-ai-novels-status-${n.id}`}
                          className={`rounded-full px-1.5 py-[1px] text-[9.5px] font-semibold uppercase tracking-wide ${
                            n.status === 'published' ? 'bg-success/15 text-success' : 'bg-muted text-muted-foreground'
                          }`}
                        >
                          {n.status === 'published' ? 'Live' : 'Draft'}
                        </span>
                        {opening === n.id && <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />}
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
